/**
 * Read an explicit taxable flag from a row object (first matching key wins).
 * Only returns fallback when no key is a boolean — never coerces undefined to false.
 */
export function readResolveTaxable(
  row: Record<string, unknown> | null | undefined,
  keys: string[],
  fallback = true
): boolean {
  if (!row) return fallback;
  for (const k of keys) {
    const v = row[k];
    if (typeof v === "boolean") return v;
  }
  return fallback;
}

const TAXABLE_KEYS = ["taxable", "is_taxable", "specialfee_taxable", "shipping_taxable"] as const;

/** First source that has an explicit boolean taxable flag wins (catalog → UI row → resolve echo). */
export function resolveTaxableFromSources(
  sources: Array<Record<string, unknown> | null | undefined>,
  keys: string[] = [...TAXABLE_KEYS],
  fallback = true
): boolean {
  for (const source of sources) {
    if (!source) continue;
    for (const k of keys) {
      if (typeof source[k] === "boolean") return source[k] as boolean;
    }
  }
  return fallback;
}

export function findCatalogRowById(
  catalog: unknown[] | null | undefined,
  id: string,
  idKeys = ["specialfee_id", "id", "_id", "shipping_id", "shipping__id"]
): Record<string, unknown> | undefined {
  const sid = String(id || "").trim();
  if (!sid || !Array.isArray(catalog)) return undefined;
  return (catalog as Record<string, unknown>[]).find((row) =>
    idKeys.some((k) => String(row[k] ?? "") === sid)
  );
}

const SHIPPING_TAXABLE_KEYS = ["shipping_taxable", "taxable", "is_taxable"] as const;
const FEE_TAXABLE_KEYS = ["specialfee_taxable", "taxable", "is_taxable"] as const;

/** Resolve shipping taxable for API/resolve payloads (UI row + optional shipping-costs catalog). */
export function resolveShippingTaxableForPayload(
  row: Record<string, unknown> | null | undefined,
  shippingCostCatalog?: unknown[] | null
): boolean {
  const id = String(row?.shipping__id ?? row?.shipping_id ?? "");
  const catalogRow = findCatalogRowById(shippingCostCatalog, id, ["_id", "id", "shipping_id"]);
  return resolveTaxableFromSources([row, catalogRow], [...SHIPPING_TAXABLE_KEYS], true);
}

/** Resolve special-fee taxable for API/resolve payloads (UI row + optional vendor fee catalog). */
export function resolveSpecialFeeTaxableForPayload(
  row: Record<string, unknown> | null | undefined,
  specialFeeCatalog?: unknown[] | null
): boolean {
  const id = String(row?.specialfee_id ?? row?.id ?? row?._id ?? "");
  const catalogRow = findCatalogRowById(specialFeeCatalog, id, ["id", "_id", "specialfee_id"]);
  return resolveTaxableFromSources([row, catalogRow], [...FEE_TAXABLE_KEYS], true);
}

/** Read explicit required flag from a row (defaults when absent). */
export function readResolveRequired(
  row: Record<string, unknown> | null | undefined,
  fallback = false
): boolean {
  if (!row) return fallback;
  if (typeof row.required === "boolean") return row.required;
  return fallback;
}

/** Resolve special-fee required for API/resolve payloads (catalog → UI row). */
export function resolveSpecialFeeRequiredForPayload(
  row: Record<string, unknown> | null | undefined,
  specialFeeCatalog?: unknown[] | null
): boolean {
  const id = String(row?.specialfee_id ?? row?.id ?? row?._id ?? "");
  const catalogRow = findCatalogRowById(specialFeeCatalog, id, ["id", "_id", "specialfee_id"]);
  for (const source of [catalogRow, row]) {
    if (!source) continue;
    if (typeof source.required === "boolean") return source.required;
  }
  return false;
}

/** Installation/removal line taxable from saved line or item_tax_info. */
export function readInstallationLineTaxable(
  li: Record<string, unknown> | null | undefined,
  fallback = true
): boolean {
  const taxInfo = li?.item_tax_info;
  const taxInfoRow = (
    Array.isArray(taxInfo) ? taxInfo[0] : taxInfo
  ) as Record<string, unknown> | null | undefined;
  return resolveTaxableFromSources(
    [li, taxInfoRow],
    ["is_taxable", "taxable"],
    fallback
  );
}

/** Sales catalog line taxable: datatable picker → inventory product → UI row → saved item_tax_info. */
export function resolveCatalogProductLineTaxable(args: {
  pickerPayload?: Record<string, unknown> | null;
  catalogProduct?: Record<string, unknown> | null;
  row?: Record<string, unknown> | null;
  savedTaxInfo?: Record<string, unknown> | null;
}): boolean {
  return resolveTaxableFromSources(
    [args.pickerPayload, args.catalogProduct, args.row, args.savedTaxInfo],
    ["is_taxable", "taxable"],
    true
  );
}
